import React from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { fetchers } from "../lib/api";
import AdSlot from "../components/AdSlot";
import NewsCard from "../components/NewsCard";

export default function NewsDetail() {
  const { slug } = useParams();
  const { data: article, isLoading } = useQuery({
    queryKey: ["article", slug],
    queryFn: () => fetchers.article(slug),
  });
  const { data: related } = useQuery({
    queryKey: ["news-related"],
    queryFn: () => fetchers.news({ limit: 6 }),
  });

  if (isLoading) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="text-sm text-muted-foreground">Loading article…</div>
      </div>
    );
  }
  if (!article) return null;

  const paragraphs = (article.body || "").split("\n\n");
  const relatedItems = (related?.items || []).filter((a) => a.slug !== slug).slice(0, 3);

  return (
    <div className="animate-fade-up">
      <article className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">
        <Link
          to="/news"
          className="link-underline mb-6 inline-flex items-center gap-1 overline-eyebrow text-muted-foreground hover:text-primary"
          data-testid="back-to-news"
        >
          <ArrowLeft size={14} /> Back to News
        </Link>
        <div className="overline-eyebrow text-primary">{article.category}</div>
        <h1 className="mt-2 font-display text-5xl uppercase leading-none sm:text-6xl">
          {article.title}
        </h1>
        <div className="mt-4 text-xs uppercase tracking-widest text-muted-foreground">
          {article.author} · {article.date}
        </div>
        <div className="my-6 aspect-[16/9] overflow-hidden bg-secondary">
          <img src={article.image} alt={article.title} className="h-full w-full object-cover" />
        </div>

        <AdSlot size="responsive" slotId={`article-${slug}-top`} className="my-6" />

        <div className="prose prose-invert max-w-none text-base leading-relaxed">
          {paragraphs.map((p, i) => (
            <React.Fragment key={i}>
              <p className="mb-6 text-foreground/90">{p}</p>
              {i === 0 && (
                <AdSlot
                  size="rectangle"
                  slotId={`article-${slug}-inline`}
                  className="my-6"
                />
              )}
            </React.Fragment>
          ))}
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          {(article.tags || []).map((t) => (
            <span key={t} className="border border-border bg-card px-2 py-1 overline-eyebrow text-muted-foreground">
              {t}
            </span>
          ))}
        </div>

        <AdSlot size="responsive" slotId={`article-${slug}-bottom`} className="my-10" />
      </article>

      <section className="mx-auto max-w-7xl px-4 pb-12 sm:px-6 lg:px-8">
        <h2 className="mb-4 border-b border-border pb-3 font-display text-3xl uppercase">
          Keep reading
        </h2>
        <div className="grid gap-6 md:grid-cols-3">
          {relatedItems.map((a) => (
            <NewsCard key={a.id} article={a} />
          ))}
        </div>
      </section>
    </div>
  );
}
